'use client'
import { memo, useMemo } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { motion, Variants } from 'motion/react'
import { Dropdown } from '@/components/dropdown/Dropdown'
import { mq, vw, getP16, getP20_1, createUnderline } from '@/styles'
import type { TypeLang } from '@/utils'
import { useTranslator } from '@/hooks'
import { getSeasonSchedule } from '@/utils'

const corsoOptions = [
  {value: 'ES', path:'/'},
  {value: 'CAT', path:'/cat'},
  {value: 'EN', path:'/en'},
  {value: 'FR', path:'/fr'}
]

const A = styled(motion.a)<{ $bold: boolean }>`
  /* font-weight: ${$bold => ( $bold ? 'bold' : 'normal')}; */
  transition: font-variation-settings 800ms ease-in;

  /* &:hover {
    font-variation-settings: 'wght' 600 !important;
  } */

  &:hover {
    text-shadow:
    -.5px -.5px 0 var(--color-text),
    .5px -.5px 0 var(--color-text),
    -.5px .5px 0 var(--color-text),
    .5px .5px 0 var(--color-text);
    -webkit-transition: all .5s;
    -moz-transition: all .5s;
    -o-transition: all .5s;
    transition: all .5s;
  }
`

const variants:Variants = {
  initial: {
    fontWeight: 'normal'
  },
  active: {
    // fontWeight: 'bold',
    transition: {
      ease: 'easeIn',
      duration: .8
    }
  }
} as const

export const getFooterLinks = (lang) => {
  const options = corsoOptions
  return(
    <>
      <WebList>
        <li><A variants={variants} initial={'initial'} whileHover={'active'} $bold={false} href='https://luzio.es/'>LUZIO Store</A></li>
        <li><A variants={variants} initial={'initial'} whileHover={'active'} $bold={false} href='https://luzio.studio/'>LUZIO Studio</A></li>
        <li><A variants={variants} initial={'initial'} whileHover={'active'} $bold={false} href='https://iluzione.com/'>ILUZIONE - Ferrán Agulló</A></li>
        {lang === 'es' && <li><A variants={variants} initial={'initial'} whileHover={'active'} $bold={false} href='https://blog.iluzione.com/'>Blog</A></li>}
      </WebList>
      <Dropdown className={'dropdown'} trigger={lang.toUpperCase()} options={options.map(option => ({ value: option.value, path: option.path, selected: option.value.toLowerCase() === lang }))} direction={'up'}/>
    </>
  )
}

export const getFooterInfo = (t, season) => {
  return (
    <InfoWrapper>
      <div>
        {season === 'summer' ? <p>{t('layout.footer.weekdays_summer')}</p> :  <p>{t('layout.footer.weekdays_winter')}</p> }
        <a href='https://goo.gl/maps/ZREoJUWHu8i2b5a76'>
          <span>{t('layout.footer.direction')}</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.95001 4.08004L15.47 10.6C16.24 11.37 16.24 12.63 15.47 13.4L8.95001 19.92" stroke="white" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
      </div>
      <div>
        <span>{t('layout.footer.groups')}</span>
        <p>
          <span> {t('layout.footer.contact')}</span> <a href={`mailto:${t('layout.footer.mail')}`}>{t('layout.footer.mail')}<span></span></a>
          <span> {t('layout.footer.or')}</span> <a href={`tel:+${t('layout.footer.phone_link')}`}>{t('layout.footer.phone_number')}<span></span> </a>
        </p>
      </div>
      <div>
        <a href={`https://wa.me/c/${t('layout.footer.phone_link')}`}>
          <span>{t('layout.main.whatsapp')}</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.95001 4.08004L15.47 10.6C16.24 11.37 16.24 12.63 15.47 13.4L8.95001 19.92" stroke="white"strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
        <a href={'https://www.tiktok.com/@iluzione_restaurant'}>
          <span>TikTok</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.95001 4.08004L15.47 10.6C16.24 11.37 16.24 12.63 15.47 13.4L8.95001 19.92" stroke="white"strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
      </div>
    </InfoWrapper>
  )
}


const seasonInfo = getSeasonSchedule('corso')
export const Footer = memo(() => {
  const { lang } = useParams() as { lang: TypeLang }
  const { t } = useTranslator()
  const FooterLinks = useMemo(() => getFooterLinks(lang), [lang])

  return (
    <FooterStyled>
      {getFooterInfo(t, seasonInfo)}
      {FooterLinks}
    </FooterStyled>
  )
})

const WebList = styled.ul`
  ${getP16}
  display: flex;
  flex-direction: column;
  width: 80%;

  @media screen and (min-width: 768px) and (max-width: 1170px) {
    font-size: 18px;
  }

  ${mq.greaterThan('tablet')} {
    flex-direction: row;
    width: 100%;
    justify-content: flex-end;
  }

  ${mq.greaterThan('container')} {
    font-size: 18px;
  }

  > li {
    font-weight: 400;
    margin-top:${vw(20, 'mobile')};
    font-family: "Work Sans", sans-serif;

    ${mq.greaterThan('tablet')} {
      margin-top: unset;
    }

  }
  > li:not(li:last-of-type) {
    margin-right: unset;

    ${mq.greaterThan('tablet')} {
      margin-right: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-right: 24.3px;
    }
  }
`
const FooterStyled = styled.footer`
  display: none;

  ${mq.greaterThan('tablet')} {
    align-items: flex-end;
    bottom: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: ${vw(10, 'desktop')} ${vw(50, 'desktop')} ${vw(25, 'desktop')};
    position: fixed;
    width: 100%;
    z-index: 92;
  }

  ${mq.greaterThan('desktop')} {
    padding: 10px 50px 25px;
  }

  .dropdown {
    display: flex;
    flex-direction: row;

    ${mq.greaterThan('tablet')} {
      display:none;
    }
  }
`

export const InfoWrapper = styled.div`
  width: 98%;
  ${mq.greaterThan('tablet')} {
    width: 60%;
  }

  ${mq.greaterThan('container')} {
      width:100%;
  }

  p, a, span {
    ${getP20_1}
    @media screen and (min-width: 768px) and (max-width: 1170px) {
      font-size: 19px;
      line-height: 23px;
    }

    ${mq.greaterThan('container')} {
      font-size: 20px;
      line-height: 24px;
    }
  }

  div:nth-child(2),  div:nth-child(3){
    margin-top: ${vw(30, 'mobile')};

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 30px;
    }
  }

  a {
    align-items: center;
    display: flex;
    flex-direction: row;

    svg {
      display: block;
      height:${vw(24, 'mobile')};
      margin-left:${vw(5, 'mobile')};
      width:${vw(24, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        height: 34.9px;
        margin-left: 7.2px;
        width: 34.9px;
      }

      ${mq.greaterThan('tablet')} {
        height:${vw(24, 'desktop')};
        margin-left:${vw(5, 'desktop')};
        width:${vw(24, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        height: 24px;
        margin-left: 5px;
        width: 24px;
      }
    }

    &:hover {
      svg {
        stroke-width:2;
        transform:translateX(${vw(5, 'mobile')});
        transition: transform 600ms ease-in-out, stroke-width 600ms ease-in-out;

        ${mq.greaterThan('tablet')} {
          transform:translateX(${vw(5, 'desktop')});
        }
      }
    }

  }

  div:nth-child(3) {
    display: block;

    ${mq.greaterThan('tablet')} {
      display: none;
    }
  }

  p > a {
    display:inline;
  }

  div:nth-of-type(2) p a {
    ${createUnderline({ color: 'white', reverse: true })}
  }
`
